import { useBoltApiClient } from "@bolt/core";
import { PdfViewerComponent } from "@syncfusion/ej2-react-pdfviewer";
import {
  AppToasterV2,
  dialogStore,
  LoadingV2,
  PdfTemplateDesigner,
  SecondaryButton,
  useDocumentTemplateSchema,
  AnnotationDefaults,
} from "@ucl/library";
import React, { useRef } from "react";
import { useBoltDocumentTemplateDesigner } from "./useBoltDocumentTemplateDesigner";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { BoltDocumentTemplateFormUpdate } from "../BoltDocumentTemplateForm/BoltDocumentTemplateFormUpdate";

export interface BoltDocumentTemplateDesignerProps {
  templateId: string;
  modelName: string;
  entityType: string;
  previewBaseODataUrl?: string;
  previewODataKey?: string;
  previewGetAuthRequestHeader?: () => Promise<
    Record<string, string> | undefined
  >;
  onSave?: () => void;
  onDelete?: () => void;
  zoomValue?: number;
  annotationDefaults?: AnnotationDefaults;
}

export const BoltDocumentTemplateDesigner: React.FC<
  BoltDocumentTemplateDesignerProps
> = (props) => {
  const boltApiClient = useBoltApiClient();
  const pdfViewerRemoteRef = useRef<PdfViewerComponent>(null);
  const dialogId = "update-document-template-dialog";

  const {
    getFlattenedDocumentTemplateSchema,
    getDocumentTemplateFieldsData,
    updateDocumentTemplateFileFormData,
  } = useDocumentTemplateSchema(boltApiClient);

  const { template, file, isLoading, getTemplate } =
    useBoltDocumentTemplateDesigner(props, boltApiClient);

  return (
    <section className="bolt-document-template-designer">
      {isLoading && <LoadingV2 loadingType="spinner" />}
      {!isLoading && !template && <div>No template found</div>}
      {!isLoading && template && file && (
        <PdfTemplateDesigner
          id="bolt-document-template-designer"
          pdfViewerRef={pdfViewerRemoteRef}
          serviceUrl={`${boltApiClient.baseApiUrl}/pdf-viewer`}
          fileFormData={template?.fileFormData || ""}
          zoomValue={props.zoomValue}
          getTemplateFields={() =>
            getFlattenedDocumentTemplateSchema(props.modelName)
          }
          getTemplateFieldsData={(id?: string) => {
            if (id) {
              return getDocumentTemplateFieldsData(props.entityType, id);
            }
            return Promise.resolve({});
          }}
          fileDocument={file}
          onSave={async (fileFormData) => {
            updateDocumentTemplateFileFormData(template.id, fileFormData || "")
              .then(() => {
                AppToasterV2.showSuccessAppToaster("Template updated.");
              })
              .catch((error) => {
                AppToasterV2.showFailedActionToaster(error.message);
              });
          }}
          previewBaseODataUrl={props.previewBaseODataUrl}
          previewODataKey={props.previewODataKey}
          previewGetAuthRequestHeader={props.previewGetAuthRequestHeader}
          annotationDefaults={props.annotationDefaults}
          designerHeaderContent={
            <div className="bolt-document-template-designer-header">
              <span>{template.templateName}</span>
              <SecondaryButton
                minimal={true}
                iconProps={{
                  icon: faEdit,
                  size: "sm",
                }}
                onClick={() => {
                  dialogStore.openDialog({
                    id: dialogId,
                    header: "Update Document Template",
                    content: () => (
                      <BoltDocumentTemplateFormUpdate
                        templateId={template.id}
                        onSave={async () => {
                          await getTemplate(template.id);
                          props.onSave?.();
                          dialogStore.closeDialog(dialogId);
                        }}
                        onDelete={async () => {
                          await props.onDelete?.();
                          dialogStore.closeDialog(dialogId);
                        }}
                        onCancel={() => {
                          dialogStore.closeDialog(dialogId);
                        }}
                      />
                    ),
                    close: () => {
                      dialogStore.closeDialog(dialogId);
                    },
                  });
                }}
              />
            </div>
          }
        />
      )}
    </section>
  );
};
